import React, { useState } from 'react';
import { HelpCircleIcon, AlertCircleIcon, ActivityIcon, ClockIcon, BarChart2Icon, ChevronDownIcon, ChevronUpIcon, PhoneCallIcon, UploadIcon, FileTextIcon, PlayIcon, BookOpenIcon, SendIcon, UserIcon } from 'lucide-react';
// Section component for collapsible sections on mobile
interface SectionProps {
  title: React.ReactNode;
  children: React.ReactNode;
  icon: React.ReactNode;
}
const Section: React.FC<SectionProps> = ({
  title,
  children,
  icon
}) => {
  const [isOpen, setIsOpen] = useState(window.innerWidth >= 640);
  return <section data-id="element-81">
      <div className="flex items-center justify-between mb-4 cursor-pointer sm:cursor-default" onClick={() => setIsOpen(!isOpen)} data-id="element-82">
        <h3 className="text-base sm:text-lg font-medium text-gray-900 flex items-center" data-id="element-83">
          {icon}
          {title}
        </h3>
        <div className="sm:hidden" data-id="element-84">
          {isOpen ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-85" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-86" />}
        </div>
      </div>
      {isOpen && children}
    </section>;
};
// Chatbot component
const Chatbot: React.FC = () => {
  const [messages, setMessages] = useState<Array<{
    text: string;
    isUser: boolean;
  }>>([{
    text: 'Hello! How can I help you with your wound care today?',
    isUser: false
  }]);
  const [inputText, setInputText] = useState('');
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) return;
    // Add user message
    setMessages(prev => [...prev, {
      text: inputText,
      isUser: true
    }]);
    // Simulate bot response
    setTimeout(() => {
      const botResponses = ["I understand your concern. It's normal to see some redness around the wound during the healing process. If the redness is spreading or accompanied by increased pain, please contact your healthcare provider.", "The AiDetect system monitors changes in your wound's status over time. A yellow indicator suggests increased inflammation that should be monitored closely.", "Make sure to keep your wound clean and follow your doctor's care instructions. The AiDetect sensor should remain securely attached.", 'If your wound temperature is elevated, this could indicate increased inflammation. The app will show this as a yellow or red status depending on severity.', "I recommend checking the 'Understanding Wound Infection Risk' section in the Help tab for more information on interpreting your readings."];
      const randomResponse = botResponses[Math.floor(Math.random() * botResponses.length)];
      setMessages(prev => [...prev, {
        text: randomResponse,
        isUser: false
      }]);
    }, 1000);
    setInputText('');
  };
  return <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-id="element-87">
      <div className="bg-white rounded-lg border border-gray-200 h-60 sm:h-72 overflow-y-auto p-3 mb-3" data-id="element-88">
        {messages.map((message, index) => <div key={index} className={`mb-2 ${message.isUser ? 'text-right' : 'text-left'}`} data-id="element-89">
            <div className={`inline-block px-3 py-2 rounded-lg text-xs sm:text-sm max-w-[80%] ${message.isUser ? 'bg-blue-500 text-white rounded-br-none' : 'bg-gray-100 text-gray-800 rounded-bl-none'}`} data-id="element-90">
              {message.text}
            </div>
          </div>)}
      </div>
      <form onSubmit={handleSendMessage} className="flex" data-id="element-91">
        <input type="text" value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Type your question here..." className="flex-1 text-xs sm:text-sm border border-gray-300 rounded-l-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-92" />
        <button type="submit" className="bg-blue-500 text-white px-3 py-2 rounded-r-lg hover:bg-blue-600 transition-colors" data-id="element-93">
          <SendIcon className="h-4 w-4 sm:h-5 sm:w-5" data-id="element-94" />
        </button>
      </form>
    </div>;
};
export const HelpPage: React.FC = () => {
  return <div className="bg-white shadow rounded-lg" data-id="element-95">
      <div className="px-3 py-4 sm:px-4 sm:py-5 md:p-6" data-id="element-96">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6" data-id="element-97">
          Help & Information
        </h2>
        <div className="space-y-6 sm:space-y-8" data-id="element-98">
          <Section title="Engage with Care Team" icon={<PhoneCallIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-100" />} data-id="element-99">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-id="element-101">
              <div className="space-y-4" data-id="element-102">
                <div className="flex items-start" data-id="element-103">
                  <div className="flex-shrink-0" data-id="element-104">
                    <PhoneCallIcon className="h-5 w-5 text-blue-500" data-id="element-105" />
                  </div>
                  <div className="ml-3" data-id="element-106">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-900" data-id="element-107">
                      Call Care Provider
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-500" data-id="element-108">
                      Connect directly with your healthcare team for questions
                      or concerns
                    </p>
                    <button className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors" data-id="element-109">
                      Call Now
                    </button>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-110">
                  <div className="flex-shrink-0" data-id="element-111">
                    <UploadIcon className="h-5 w-5 text-blue-500" data-id="element-112" />
                  </div>
                  <div className="ml-3" data-id="element-113">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-900" data-id="element-114">
                      Upload Images to EHR
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-500" data-id="element-115">
                      Share wound images securely with your healthcare provider
                    </p>
                    <button className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors" data-id="element-116">
                      Upload Images
                    </button>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-117">
                  <div className="flex-shrink-0" data-id="element-118">
                    <FileTextIcon className="h-5 w-5 text-blue-500" data-id="element-119" />
                  </div>
                  <div className="ml-3" data-id="element-120">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-900" data-id="element-121">
                      Access Documentation
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-500" data-id="element-122">
                      View care instructions and documentation from your
                      provider
                    </p>
                    <button className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors" data-id="element-123">
                      View Documents
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Section>
          <Section title="iDetect Usage Guides" icon={<PlayIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-125" />} data-id="element-124">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-id="element-126">
              <p className="text-xs sm:text-sm text-gray-700 mb-3" data-id="element-127">
                Watch these instructional videos to learn how to use your
                AiDetect system effectively:
              </p>
              <ul className="space-y-3" data-id="element-128">
                <li data-id="element-129">
                  <a href="#" className="flex items-center text-blue-600 hover:underline text-xs sm:text-sm" data-id="element-130">
                    <PlayIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-131" />
                    Using the Applicator to Attach Device
                  </a>
                </li>
                <li data-id="element-132">
                  <a href="#" className="flex items-center text-blue-600 hover:underline text-xs sm:text-sm" data-id="element-133">
                    <PlayIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-134" />
                    Removing & Reconnecting the Interrogator
                  </a>
                </li>
                <li data-id="element-135">
                  <a href="#" className="flex items-center text-blue-600 hover:underline text-xs sm:text-sm" data-id="element-136">
                    <PlayIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-137" />
                    Navigating the App
                  </a>
                </li>
                <li data-id="element-138">
                  <a href="#" className="flex items-center text-blue-600 hover:underline text-xs sm:text-sm" data-id="element-139">
                    <PlayIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-140" />
                    Uploading Images
                  </a>
                </li>
              </ul>
            </div>
          </Section>
          <Section title="Learn More About Wound Healing" icon={<BookOpenIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-142" />} data-id="element-141">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-id="element-143">
              <p className="text-xs sm:text-sm text-gray-700 mb-3" data-id="element-144">
                Explore these educational resources to better understand the
                wound healing process:
              </p>
              <ul className="space-y-3" data-id="element-145">
                <li data-id="element-146">
                  <a href="#" className="flex items-center text-blue-600 hover:underline text-xs sm:text-sm" data-id="element-147">
                    <PlayIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-148" />
                    How Your Body Heals
                  </a>
                </li>
                <li data-id="element-149">
                  <a href="#" className="flex items-center text-blue-600 hover:underline text-xs sm:text-sm" data-id="element-150">
                    <PlayIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-151" />
                    Signs of Normal vs. Abnormal Healing
                  </a>
                </li>
                <li data-id="element-152">
                  <a href="#" className="flex items-center text-blue-600 hover:underline text-xs sm:text-sm" data-id="element-153">
                    <PlayIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-154" />
                    Understanding Wound Types
                  </a>
                </li>
              </ul>
            </div>
          </Section>
          <Section title="About This Application" icon={<HelpCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-156" />} data-id="element-155">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-id="element-157">
              <p className="text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4" data-id="element-158">
                The AiDetect system is designed to track and visualize patient
                wound infection risk through continuous monitoring of wound
                status. The system uses a color-coded status indicator to
                provide an at-a-glance assessment of patient condition.
              </p>
              <p className="text-xs sm:text-sm text-gray-700" data-id="element-159">
                This application allows medical professionals to monitor changes
                over time and identify when a patient's wound begins to show
                signs of infection, helping with diagnosis and treatment
                planning.
              </p>
            </div>
          </Section>
          <Section title="Understanding Status Indicators" icon={<AlertCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-161" />} data-id="element-160">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-id="element-162">
              <div className="space-y-3 sm:space-y-4" data-id="element-163">
                <div className="flex items-start" data-id="element-164">
                  <div className="flex-shrink-0" data-id="element-165">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full" data-id="element-166"></div>
                  </div>
                  <div className="ml-3" data-id="element-167">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-900" data-id="element-168">
                      Green (Baseline)
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-500" data-id="element-169">
                      Indicates normal wound healing with low infection risk
                      (below 30%).
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-170">
                  <div className="flex-shrink-0" data-id="element-171">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-yellow-500 rounded-full" data-id="element-172"></div>
                  </div>
                  <div className="ml-3" data-id="element-173">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-900" data-id="element-174">
                      Yellow (Inflamed)
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-500" data-id="element-175">
                      Indicates potential inflammation with moderate infection
                      risk (30-60%). Increased monitoring recommended.
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-176">
                  <div className="flex-shrink-0" data-id="element-177">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-red-500 rounded-full" data-id="element-178"></div>
                  </div>
                  <div className="ml-3" data-id="element-179">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-900" data-id="element-180">
                      Red (Infection Risk)
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-500" data-id="element-181">
                      Indicates high infection risk (above 60%). Immediate
                      attention required.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Section>
          <Section title="Understanding Wound Infection Risk" icon={<ActivityIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-183" />} data-id="element-182">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-id="element-184">
              <p className="text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4" data-id="element-185">
                Wound infection risk is measured as a percentage from 0% to
                100%, representing the likelihood of infection developing in a
                wound based on various biomarkers and visual indicators.
              </p>
              <p className="text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4" data-id="element-186">
                <strong data-id="element-187">Key points to understand:</strong>
              </p>
              <ul className="list-disc pl-4 sm:pl-5 space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-700" data-id="element-188">
                <li data-id="element-189">
                  Risk below 30% generally indicates normal healing progression
                </li>
                <li data-id="element-190">
                  Risk between 30-60% indicates inflammation that may require
                  intervention
                </li>
                <li data-id="element-191">
                  Risk above 60% (high risk threshold) indicates likely
                  infection requiring immediate attention
                </li>
                <li data-id="element-192">
                  The Raw Data tab provides detailed visualization of risk over
                  time
                </li>
              </ul>
            </div>
          </Section>
          <Section title="Using the Time Slider" icon={<ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-194" />} data-id="element-193">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-id="element-195">
              <p className="text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4" data-id="element-196">
                The time slider allows you to view patient data at different
                points in time:
              </p>
              <ul className="list-disc pl-4 sm:pl-5 space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-700" data-id="element-197">
                <li data-id="element-198">Drag the slider left to view earlier measurements</li>
                <li data-id="element-199">Drag the slider right to view more recent measurements</li>
                <li data-id="element-200">
                  The colored sections of the slider track indicate when status
                  changes occurred
                </li>
                <li data-id="element-201">
                  Use this feature to identify when a patient's wound began to
                  show signs of infection
                </li>
              </ul>
            </div>
          </Section>
          <Section title="Interpreting Vital Signs" icon={<BarChart2Icon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-203" />} data-id="element-202">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-id="element-204">
              <p className="text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4" data-id="element-205">
                The dashboard displays temperature as a key vital sign that
                should be interpreted alongside the wound infection risk data:
              </p>
              <div className="overflow-x-auto -mx-3 sm:mx-0" data-id="element-206">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-207">
                  <thead className="bg-gray-50" data-id="element-208">
                    <tr data-id="element-209">
                      <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-210">
                        Vital Sign
                      </th>
                      <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-211">
                        Baseline
                      </th>
                      <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-212">
                        Inflamed
                      </th>
                      <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-213">
                        Infection Warning
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-214">
                    <tr data-id="element-215">
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900" data-id="element-216">
                        Temp
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500" data-id="element-217">
                        97.8-99.1°F
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500" data-id="element-218">
                        99.1-100.4°F
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500" data-id="element-219">
                        &gt;100.4°F
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
          <Section title="Need Help with Something Else?" icon={<UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 flex-shrink-0" data-id="element-221" />} data-id="element-220">
            <Chatbot data-id="element-222" />
          </Section>
        </div>
      </div>
    </div>;
};